import {
  type ComponentProps,
  type PropsWithChildren,
  useState,
  useMemo,
  useEffect,
  useCallback,
} from 'react';
import { Link } from 'react-router-dom';
import { EntityCatalogGraphCard } from '@backstage/plugin-catalog-graph';
import {
  CompoundEntityRef,
  DEFAULT_NAMESPACE,
  Entity,
  RELATION_CHILD_OF,
  RELATION_CONSUMES_API,
  RELATION_DEPENDS_ON,
  RELATION_HAS_MEMBER,
  RELATION_HAS_PART,
  RELATION_OWNED_BY,
  RELATION_OWNER_OF,
  RELATION_PARENT_OF,
  RELATION_PROVIDES_API,
  parseEntityRef,
  stringifyEntityRef,
} from '@backstage/catalog-model';
import { useApi } from '@backstage/frontend-plugin-api';
import {
  catalogApiRef,
  getEntityRelations,
  useEntity,
} from '@backstage/plugin-catalog-react';
import { TdsIcon } from '@scania/tegel-react';
import {
  GridEntityAboutCard,
  type GridEntityCardVariant,
} from './GridEntityAboutCard';
import { TegelCard, TegelTable, type TegelTableColumn } from '../common/Tegel';
import {
  entityDisplayName,
  entityUserEmail,
  humanizedRelationLabel,
  relationEntityRef,
  titleCaseEntityName,
  useResolvedEntityLabels,
} from './entityDisplay';
import { catalogEntityRoutePath } from '../Root/navigation';

type GridEntityCatalogGraphCardProps = ComponentProps<
  typeof EntityCatalogGraphCard
> & {
  variant?: GridEntityCardVariant;
};

export type GridEntityRelationColumn =
  | 'description'
  | 'kind'
  | 'lifecycle'
  | 'name'
  | 'owner'
  | 'type';

export type GridEntityRelationCardProps = {
  columns?: GridEntityRelationColumn[];
  emptyMessage: string;
  kind?: string;
  relationType: string;
  tableId: string;
  title: string;
  variant?: GridEntityCardVariant;
};

type GridEntityUserProfileCardProps = {
  variant?: GridEntityCardVariant;
};

type GridEntityGroupProfileCardProps = {
  variant?: GridEntityCardVariant;
};

type GridEntityOwnershipCardProps = {
  variant?: GridEntityCardVariant;
};

type GridEntityMembersListCardProps = {
  variant?: GridEntityCardVariant;
};

type GridEntityLinksCardProps = {
  variant?: GridEntityCardVariant;
};

type MetadataLink = {
  icon?: string;
  title?: string;
  type?: string;
  url?: string;
};

const gridCardClassName = (variant: GridEntityCardVariant) =>
  variant === 'fullHeight'
    ? 'catalog-grid-card catalog-full-height-card catalog-entity-card-surface'
    : 'catalog-grid-card catalog-entity-card-surface';

const gridCardClassNames = (
  variant: GridEntityCardVariant,
  className?: string,
) => [gridCardClassName(variant), className].filter(Boolean).join(' ');

const tegelWrappedCardClassNames = (
  variant: GridEntityCardVariant,
  className?: string,
) =>
  gridCardClassNames(
    variant,
    ['catalog-tegel-card-shell', className].filter(Boolean).join(' '),
  );

const entityPath = (entityOrRef: Entity | CompoundEntityRef | string) => {
  if (typeof entityOrRef === 'string') {
    return catalogEntityRoutePath(
      parseEntityRef(entityOrRef, {
        defaultKind: 'component',
        defaultNamespace: DEFAULT_NAMESPACE,
      }),
    );
  }

  const ref =
    'metadata' in entityOrRef
      ? {
          kind: entityOrRef.kind,
          namespace: entityOrRef.metadata.namespace ?? DEFAULT_NAMESPACE,
          name: entityOrRef.metadata.name,
        }
      : {
          kind: entityOrRef.kind,
          namespace: entityOrRef.namespace ?? DEFAULT_NAMESPACE,
          name: entityOrRef.name,
        };

  return catalogEntityRoutePath(ref);
};

const relationSummary = (relation: CompoundEntityRef) =>
  titleCaseEntityName(relation.name);

const metadataLinks = (entity: Entity) =>
  ((entity.metadata.links ?? []) as MetadataLink[]).filter(link => link.url);

const EmptyCardState = ({ children }: PropsWithChildren<{}>) => (
  <p className="tegel-entity-card-empty">{children}</p>
);

type EntitySpec = {
  lifecycle?: string;
  owner?: string;
  type?: string;
};

type UserEntitySpec = {
  profile?: {
    picture?: string;
  };
};

export type RelationTableRow = {
  description: string;
  entity?: Entity;
  href: string;
  kind: string;
  lifecycle: string;
  name: string;
  owner: string;
  relation: CompoundEntityRef;
  type: string;
};

const entitySpec = (entity?: Entity) => entity?.spec as EntitySpec | undefined;

const stripEntityPrefix = (value?: string) =>
  value
    ?.replace(/^group:/i, '')
    .replace(/^user:/i, '')
    .replace(/^system:/i, '')
    .replace(/^default\//i, '');

const relationOwner = (entity?: Entity) => {
  if (!entity) {
    return '-';
  }

  const owners = getEntityRelations(entity, RELATION_OWNED_BY);
  if (owners.length) {
    return owners
      .map(owner => humanizedRelationLabel(owner, 'group'))
      .join(', ');
  }

  return stripEntityPrefix(entitySpec(entity)?.owner) ?? '-';
};

const userAvatarLabel = (label: string) =>
  label
    .split(/[.\s_-]+/)
    .filter(Boolean)
    .map(part => part[0])
    .join('')
    .substring(0, 2)
    .toUpperCase();

const userSecondaryLabel = (
  entityOrRelation?: Entity | CompoundEntityRef,
  fallback?: string,
) => entityUserEmail(entityOrRelation) ?? fallback ?? 'No email available';

const userProfilePicture = (entity: Entity) =>
  (entity.spec as UserEntitySpec | undefined)?.profile?.picture;

const useRelationEntityMap = (relations: CompoundEntityRef[]) => {
  const catalogApi = useApi(catalogApiRef);
  const [entitiesByRef, setEntitiesByRef] = useState<
    Record<string, Entity | undefined>
  >({});
  const refsKey = useMemo(
    () =>
      Array.from(new Set(relations.map(relationEntityRef)))
        .sort()
        .join('|'),
    [relations],
  );

  useEffect(() => {
    let active = true;
    const entityRefs = refsKey ? refsKey.split('|') : [];

    if (!entityRefs.length) {
      setEntitiesByRef({});
      return () => {
        active = false;
      };
    }

    const loadEntities = async () => {
      try {
        const response = await catalogApi.getEntitiesByRefs({ entityRefs });
        if (!active) {
          return;
        }

        setEntitiesByRef(
          Object.fromEntries(
            response.items.map((entity, index) => [entityRefs[index], entity]),
          ),
        );
      } catch {
        if (active) {
          setEntitiesByRef({});
        }
      }
    };

    void loadEntities();

    return () => {
      active = false;
    };
  }, [catalogApi, refsKey]);

  return entitiesByRef;
};

const RelationName = ({ row }: { row: RelationTableRow }) => (
  <Link className="tegel-entity-table-link" to={row.href}>
    <strong>{row.name}</strong>
  </Link>
);

const relationTableColumns: Record<
  GridEntityRelationColumn,
  TegelTableColumn<RelationTableRow>
> = {
  description: {
    key: 'description',
    header: 'Description',
    render: row => row.description,
  },
  kind: {
    key: 'kind',
    header: 'Kind',
    render: row => row.kind,
    width: '14%',
  },
  lifecycle: {
    key: 'lifecycle',
    header: 'Lifecycle',
    render: row => row.lifecycle,
    width: '14%',
  },
  name: {
    key: 'name',
    header: 'Name',
    render: row => <RelationName row={row} />,
    width: '26%',
  },
  owner: {
    key: 'owner',
    header: 'Owner',
    render: row => row.owner,
    width: '16%',
  },
  type: {
    key: 'type',
    header: 'Type',
    render: row => row.type,
    width: '14%',
  },
};

const defaultRelationColumns: GridEntityRelationColumn[] = [
  'name',
  'owner',
  'type',
  'lifecycle',
  'description',
];

export const GridEntityRelationTableCard = ({
  columns = defaultRelationColumns,
  emptyMessage,
  kind,
  relationType,
  tableId,
  title,
  variant = 'gridItem',
}: GridEntityRelationCardProps) => {
  const { entity } = useEntity();
  const relations = getEntityRelations(
    entity,
    relationType,
    kind ? { kind } : undefined,
  );
  const entitiesByRef = useRelationEntityMap(relations);
  const relationLabel = useResolvedEntityLabels(
    relations,
    useCallback(relation => relationSummary(relation), []),
  );
  const rows = relations.map(relation => {
    const relatedEntity = entitiesByRef[relationEntityRef(relation)];
    const spec = entitySpec(relatedEntity);

    return {
      description: relatedEntity?.metadata.description ?? '-',
      entity: relatedEntity,
      href: entityPath(relation),
      kind: titleCaseEntityName(relatedEntity?.kind ?? relation.kind),
      lifecycle: spec?.lifecycle ?? '-',
      name: relatedEntity
        ? entityDisplayName(relatedEntity)
        : relationLabel(relation),
      owner: relationOwner(relatedEntity),
      relation,
      type: spec?.type ?? '-',
    };
  });

  return (
    <GridEntityCard
      variant={variant}
      title={title}
      bodyClassName="tegel-entity-relation-card"
    >
      {rows.length ? (
        <TegelTable
          tableId={tableId}
          className="tegel-entity-relation-table"
          rows={rows}
          getRowKey={row => relationEntityRef(row.relation)}
          columns={columns.map(column => relationTableColumns[column])}
        />
      ) : (
        <EmptyCardState>{emptyMessage}</EmptyCardState>
      )}
    </GridEntityCard>
  );
};

export { GridEntityAboutCard };

export function GridEntityCard({
  variant = 'gridItem',
  bodyClassName,
  children,
  className,
  title,
}: PropsWithChildren<{
  bodyClassName?: string;
  variant?: GridEntityCardVariant;
  className?: string;
  title?: string;
}>) {
  return title ? (
    <div className={tegelWrappedCardClassNames(variant, className)}>
      <TegelCard
        title={title}
        bodyClassName={['catalog-tegel-card-body', bodyClassName]
          .filter(Boolean)
          .join(' ')}
      >
        {children}
      </TegelCard>
    </div>
  ) : (
    <div className={gridCardClassNames(variant, className)}>{children}</div>
  );
}

export const GridEntityLinksCard = ({
  variant = 'gridItem',
}: GridEntityLinksCardProps) => {
  const { entity } = useEntity();
  const links = metadataLinks(entity);

  return (
    <GridEntityCard
      variant={variant}
      className="catalog-entity-card-surface"
      title="Links"
      bodyClassName="tegel-entity-links-card"
    >
      {links.length ? (
        <ul className="tegel-entity-link-list">
          {links.map(link => (
            <li key={`${link.title ?? link.url}-${link.url}`}>
              <a href={link.url} target="_blank" rel="noopener noreferrer">
                <span>
                  <strong>{link.title ?? link.url}</strong>
                  {link.type && <small>{link.type}</small>}
                </span>
                <TdsIcon
                  name="arrow_diagonal"
                  size="18px"
                  svgTitle="Open link"
                />
              </a>
            </li>
          ))}
        </ul>
      ) : (
        <EmptyCardState>No links defined for this entity.</EmptyCardState>
      )}
    </GridEntityCard>
  );
};

export const GridEntityCatalogGraphCard = ({
  variant = 'gridItem',
  title = 'Relations',
  ...props
}: GridEntityCatalogGraphCardProps) => (
  <GridEntityCard
    variant={variant}
    className="catalog-entity-card-surface catalog-graph-card-shell"
    title={title}
  >
    <EntityCatalogGraphCard title={title} {...props} />
  </GridEntityCard>
);

export const GridEntityHasComponentsCard = ({
  variant = 'gridItem',
}: {
  variant?: GridEntityCardVariant;
}) => (
  <GridEntityRelationTableCard
    variant={variant}
    title="Components"
    relationType={RELATION_HAS_PART}
    kind="component"
    tableId="catalog-entity-components"
    emptyMessage="No components found for this entity."
  />
);

export const GridEntityHasResourcesCard = ({
  variant = 'gridItem',
}: {
  variant?: GridEntityCardVariant;
}) => (
  <GridEntityRelationTableCard
    variant={variant}
    title="Resources"
    relationType={RELATION_HAS_PART}
    kind="resource"
    tableId="catalog-entity-resources"
    emptyMessage="No resources found for this entity."
  />
);

export const GridEntityHasSystemsCard = ({
  variant = 'gridItem',
}: {
  variant?: GridEntityCardVariant;
}) => (
  <GridEntityRelationTableCard
    variant={variant}
    title="Systems"
    relationType={RELATION_HAS_PART}
    kind="system"
    tableId="catalog-entity-systems"
    emptyMessage="No systems found for this entity."
  />
);

export const GridEntityHasApisCard = ({
  variant = 'gridItem',
}: {
  variant?: GridEntityCardVariant;
}) => (
  <GridEntityRelationTableCard
    variant={variant}
    title="APIs"
    relationType={RELATION_HAS_PART}
    kind="api"
    tableId="catalog-entity-apis"
    emptyMessage="No APIs found for this entity."
  />
);

export const GridEntityProvidedApisCard = ({
  variant = 'gridItem',
}: {
  variant?: GridEntityCardVariant;
}) => (
  <GridEntityRelationTableCard
    variant={variant}
    title="Provided APIs"
    relationType={RELATION_PROVIDES_API}
    kind="api"
    tableId="catalog-entity-provided-apis"
    emptyMessage="This entity does not provide any APIs."
  />
);

export const GridEntityConsumedApisCard = ({
  variant = 'gridItem',
}: {
  variant?: GridEntityCardVariant;
}) => (
  <GridEntityRelationTableCard
    variant={variant}
    title="Consumed APIs"
    relationType={RELATION_CONSUMES_API}
    kind="api"
    tableId="catalog-entity-consumed-apis"
    emptyMessage="This entity does not consume any APIs."
  />
);

export const GridEntityDependsOnComponentsCard = ({
  variant = 'gridItem',
}: {
  variant?: GridEntityCardVariant;
}) => (
  <GridEntityRelationTableCard
    variant={variant}
    title="Depends on components"
    relationType={RELATION_DEPENDS_ON}
    kind="component"
    tableId="catalog-entity-depends-on-components"
    emptyMessage="No component is a dependency of this component."
  />
);

export const GridEntityDependsOnResourcesCard = ({
  variant = 'gridItem',
}: {
  variant?: GridEntityCardVariant;
}) => (
  <GridEntityRelationTableCard
    variant={variant}
    title="Depends on resources"
    relationType={RELATION_DEPENDS_ON}
    kind="resource"
    tableId="catalog-entity-depends-on-resources"
    emptyMessage="No resource is a dependency of this component."
  />
);

export const GridEntityUserProfileCard = ({
  variant = 'gridItem',
}: GridEntityUserProfileCardProps) => {
  const { entity } = useEntity();
  const title = entityDisplayName(entity);
  const email = userSecondaryLabel(entity);
  const picture = userProfilePicture(entity);

  return (
    <GridEntityCard
      variant={variant}
      title="Profile"
      bodyClassName="tegel-entity-user-profile-card"
    >
      <div className="tegel-entity-user-profile-summary">
        {picture ? (
          <img src={picture} alt="" className="tegel-entity-user-avatar" />
        ) : (
          <span className="tegel-entity-user-avatar" aria-hidden="true">
            {userAvatarLabel(title || email)}
          </span>
        )}
        <div>
          <strong>{title}</strong>
          <small>{email}</small>
        </div>
      </div>
    </GridEntityCard>
  );
};

export const GridEntityGroupProfileCard = ({
  variant = 'gridItem',
}: GridEntityGroupProfileCardProps) => {
  const { entity } = useEntity();
  const parentGroups = getEntityRelations(entity, RELATION_CHILD_OF, {
    kind: 'group',
  });
  const childGroups = getEntityRelations(entity, RELATION_PARENT_OF, {
    kind: 'group',
  });
  const entityRef = stringifyEntityRef(entity);
  const parentGroupLabel = useResolvedEntityLabels(
    parentGroups,
    useCallback(relation => humanizedRelationLabel(relation, 'group'), []),
  );
  const childGroupLabel = useResolvedEntityLabels(
    childGroups,
    useCallback(relation => humanizedRelationLabel(relation, 'group'), []),
  );

  return (
    <GridEntityCard
      variant={variant}
      title="Group"
      bodyClassName="tegel-entity-group-card"
    >
      <div className="tegel-entity-group-summary">
        <span aria-hidden="true">
          <TdsIcon name="document" size="24px" svgTitle="Group" />
        </span>
        <div>
          <strong>{entityDisplayName(entity)}</strong>
          <small>{entityRef}</small>
        </div>
      </div>

      <dl className="tegel-entity-group-details">
        <div>
          <dt>Parent Group</dt>
          <dd>
            {parentGroups.length ? (
              parentGroups.map(group => (
                <Link key={stringifyEntityRef(group)} to={entityPath(group)}>
                  {parentGroupLabel(group)}
                </Link>
              ))
            ) : (
              <span>No parent group</span>
            )}
          </dd>
        </div>
        <div>
          <dt>Child Groups</dt>
          <dd>
            {childGroups.length ? (
              childGroups.map(group => (
                <Link key={stringifyEntityRef(group)} to={entityPath(group)}>
                  {childGroupLabel(group)}
                </Link>
              ))
            ) : (
              <span>No child groups</span>
            )}
          </dd>
        </div>
      </dl>
    </GridEntityCard>
  );
};

export const GridEntityOwnershipCard = ({
  variant = 'gridItem',
}: GridEntityOwnershipCardProps) => {
  const { entity } = useEntity();
  const ownedEntities = getEntityRelations(entity, RELATION_OWNER_OF);
  const ownedEntityLabel = useResolvedEntityLabels(
    ownedEntities,
    useCallback(relation => relationSummary(relation), []),
  );

  return (
    <GridEntityCard
      variant={variant}
      title="Ownership"
      bodyClassName="tegel-entity-ownership-card"
    >
      {ownedEntities.length ? (
        <div className="tegel-entity-owned-grid">
          {ownedEntities.map(relation => (
            <Link
              key={stringifyEntityRef(relation)}
              to={entityPath(relation)}
              className="tegel-entity-owned-tile"
            >
              <span>{relation.kind}</span>
              <strong>{ownedEntityLabel(relation)}</strong>
            </Link>
          ))}
        </div>
      ) : (
        <EmptyCardState>No owned entities found.</EmptyCardState>
      )}
    </GridEntityCard>
  );
};

export const GridEntityMembersListCard = ({
  variant = 'gridItem',
}: GridEntityMembersListCardProps) => {
  const { entity } = useEntity();
  const members = getEntityRelations(entity, RELATION_HAS_MEMBER, {
    kind: 'user',
  });
  const membersByRef = useRelationEntityMap(members);
  const memberLabel = useResolvedEntityLabels(
    members,
    useCallback(relation => humanizedRelationLabel(relation, 'user'), []),
  );

  return (
    <GridEntityCard
      variant={variant}
      title="Members"
      bodyClassName="tegel-entity-members-card"
    >
      {members.length ? (
        <ul className="tegel-entity-member-list">
          {members.map(member => {
            const relatedEntity = membersByRef[relationEntityRef(member)];
            const label = relatedEntity
              ? entityDisplayName(relatedEntity)
              : memberLabel(member);
            const secondaryLabel = userSecondaryLabel(relatedEntity ?? member);

            return (
              <li key={stringifyEntityRef(member)}>
                <Link to={entityPath(member)}>
                  <span
                    className="tegel-entity-member-avatar"
                    aria-hidden="true"
                  >
                    {userAvatarLabel(label)}
                  </span>
                  <span>
                    <strong>{label}</strong>
                    <small>{secondaryLabel}</small>
                  </span>
                </Link>
              </li>
            );
          })}
        </ul>
      ) : (
        <EmptyCardState>No members found for this group.</EmptyCardState>
      )}
    </GridEntityCard>
  );
};
